import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@apollo/client";
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  RadioGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { Radio, Select } from "@outpave/ds";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Center,
  Columns,
  Inline,
  Stack,
} from "components/layout/reusableLayouts";
import ErrorState from "components/common/ErrorState";
import LoadingWithoutModal from "components/common/LoadingWithoutModal";
import AccountType from "enums/AccountType";
import useGetBankAccounts, { BankAccount } from "hooks/useGetBankAccounts";
import { suggestLedgerAccountMappings } from "utils/integrations/ledgerAccountSuggestions";
import {
  GET_INTEGRATION_SYNC_SETTINGS,
  GET_LEDGER_BANK_ACCOUNTS,
  UPDATE_INTEGRATION_SYNC_SETTINGS,
} from "../../../graphql/company";

const SCOPE_EXPENSES_ONLY = "EXPENSES_ONLY";
const SCOPE_ALL_TRANSACTIONS = "ALL_TRANSACTIONS";
const POSTING_CLEARING_ACCOUNT = "CLEARING_ACCOUNT";
const POSTING_CARD_ACCOUNT = "CARD_ACCOUNT";
const CARD_POSTING_LABELS: Record<
  string,
  { title: string; description: string }
> = {
  [POSTING_CLEARING_ACCOUNT]: {
    title: "IntegrationOptionCardPostingClearingAccount",
    description: "IntegrationOptionCardPostingClearingAccountDescription",
  },
  [POSTING_CARD_ACCOUNT]: {
    title: "IntegrationOptionCardPostingCardAccount",
    description: "IntegrationOptionCardPostingCardAccountDescription",
  },
};

// Only a bank can be a side of a transfer, so a connected card has nothing to map.
const EXTERNAL_ACCOUNT_TYPES = [AccountType.BANK];

interface SyncSettingsDrawerProps {
  open: boolean;
  handleCloseDrawer: () => void;
  /** Where to put the buttons: the host drawer's footer. */
  footerHost?: HTMLElement | null;
}

interface IntegrationSyncAccount {
  accountId?: string;
  referenceId?: string;
}

interface IntegrationSyncSettings {
  scope?: string;
  cardPosting?: string | null;
  cardPostingOptions?: string[] | null;
  defaultCardPosting?: string | null;
  accounts?: IntegrationSyncAccount[];
  transfersAccountName?: string;
}

interface IntegrationSyncSettingsData {
  getIntegrationSyncSettings?: IntegrationSyncSettings;
}

interface LedgerBankAccount {
  id: string;
  name: string;
  fullName?: string;
  bankName?: string;
  accountNumber?: string;
  bankAccountLast4?: string;
}

interface LedgerBankAccountsData {
  getLedgerBankAccounts?: LedgerBankAccount[];
}

interface SavedSyncSettings {
  scope: string;
  cardPosting: string | null;
  accounts: Record<string, string>;
}

// Until an admin chooses, full sync clears so the clearing account nets; otherwise the ledger's own default applies.
const derivedCardPosting = (scope: string, ledgerDefault: string) =>
  scope === SCOPE_ALL_TRANSACTIONS ? POSTING_CLEARING_ACCOUNT : ledgerDefault;

const SyncSettingsDrawer: React.FC<SyncSettingsDrawerProps> = (props) => {
  const { open, handleCloseDrawer, footerHost } = props;
  const { t } = useTranslation();

  const [scope, setScope] = useState<string>(SCOPE_EXPENSES_ONLY);
  const [cardPosting, setCardPosting] = useState<string | null>(null);
  const [saveFailed, setSaveFailed] = useState(false);

  useEffect(() => {
    if (open) setSaveFailed(false);
  }, [open]);
  const [accountMappings, setAccountMappings] = useState<
    Record<string, string>
  >({});
  const [saved, setSaved] = useState<SavedSyncSettings>({
    scope: SCOPE_EXPENSES_ONLY,
    cardPosting: null,
    accounts: {},
  });

  const {
    data,
    error,
    refetch,
    loading: loadingSettings,
  } = useQuery<IntegrationSyncSettingsData>(GET_INTEGRATION_SYNC_SETTINGS, {
    skip: !open,
    fetchPolicy: "network-only",
  });

  const mapsTransfers = scope === SCOPE_ALL_TRANSACTIONS;

  const {
    data: ledgerData,
    error: ledgerError,
    refetch: refetchLedgerAccounts,
    loading: loadingLedgerAccounts,
  } = useQuery<LedgerBankAccountsData>(GET_LEDGER_BANK_ACCOUNTS, {
    skip: !open || !mapsTransfers,
    fetchPolicy: "network-only",
  });

  const { externalAccounts, isLoading: loadingExternalAccounts } =
    useGetBankAccounts({
      accountTypes: EXTERNAL_ACCOUNT_TYPES,
      skip: !open || !mapsTransfers,
      withBalances: false,
    });

  const [updateSettings, { loading: saving }] = useMutation(
    UPDATE_INTEGRATION_SYNC_SETTINGS,
  );

  useEffect(() => {
    const settings = data?.getIntegrationSyncSettings;
    if (!settings) return;
    const mappings: Record<string, string> = {};
    (settings.accounts || []).forEach((account) => {
      if (account.accountId && account.referenceId) {
        mappings[account.accountId] = account.referenceId;
      }
    });
    setScope(settings.scope || SCOPE_EXPENSES_ONLY);
    setCardPosting(settings.cardPosting || null);
    setAccountMappings(mappings);
    setSaved({
      scope: settings.scope || SCOPE_EXPENSES_ONLY,
      cardPosting: settings.cardPosting || null,
      accounts: mappings,
    });
  }, [data]);

  const cardPostingOptions =
    data?.getIntegrationSyncSettings?.cardPostingOptions ?? [];
  const offersCardPosting = cardPostingOptions.length > 1;
  const ledgerDefault =
    data?.getIntegrationSyncSettings?.defaultCardPosting ||
    POSTING_CLEARING_ACCOUNT;
  const effectiveCardPosting =
    cardPosting ?? derivedCardPosting(scope, ledgerDefault);
  const warnsCardAccount =
    offersCardPosting &&
    effectiveCardPosting === POSTING_CARD_ACCOUNT &&
    scope === SCOPE_ALL_TRANSACTIONS;

  const ledgerAccounts = useMemo(
    () => ledgerData?.getLedgerBankAccounts || [],
    [ledgerData],
  );

  const suggestions = useMemo(
    () =>
      suggestLedgerAccountMappings(
        externalAccounts,
        ledgerAccounts,
        accountMappings,
      ),
    [externalAccounts, ledgerAccounts, accountMappings],
  );

  // The resolver always returns a populated object, so absent data means the load never landed and
  // the form is showing its hard-coded defaults rather than the company's saved settings.
  const isLoaded = Boolean(data?.getIntegrationSyncSettings);
  const hasLoadError = Boolean(error) && !loadingSettings;

  const mappedEntries = Object.entries(accountMappings).filter(
    ([, referenceId]) => Boolean(referenceId),
  );
  const isDirty =
    scope !== saved.scope ||
    cardPosting !== saved.cardPosting ||
    mappedEntries.length !== Object.keys(saved.accounts).length ||
    mappedEntries.some(
      ([accountId, referenceId]) => saved.accounts[accountId] !== referenceId,
    );

  const accountLabel = (account: BankAccount) => {
    const name = account.nickname || account.bankName || t("Bank Account");
    return account.last4 ? `${name} ••••${account.last4}` : name;
  };

  // The ledger only nests accounts by path, so "Bank of America:Checking" reads as one name here.
  const ledgerAccountLabel = (ledger: LedgerBankAccount) => {
    const path = (ledger.fullName || ledger.name).split(":");
    const name = [ledger.bankName, ...path].filter(Boolean).join(" - ");
    const withLast4 = ledger.bankAccountLast4
      ? `${name} ••••${ledger.bankAccountLast4}`
      : name;
    return ledger.accountNumber
      ? `${withLast4} (${ledger.accountNumber})`
      : withLast4;
  };

  const transfersAccountName =
    data?.getIntegrationSyncSettings?.transfersAccountName;

  const handleSave = async () => {
    const accounts = mappedEntries.map(([accountId, referenceId]) => ({
      accountId,
      referenceId,
    }));
    setSaveFailed(false);
    try {
      await updateSettings({
        variables: {
          input: { scope, accounts, ...(cardPosting && { cardPosting }) },
        },
        refetchQueries: [{ query: GET_INTEGRATION_SYNC_SETTINGS }],
      });
    } catch (saveError) {
      console.error("Error saving integration sync settings", saveError);
      setSaveFailed(true);
      return;
    }
    handleCloseDrawer();
  };

  const renderChoice = (title: string, description: string) => (
    <Inline gap="var(--space-2)" align="center" sx={{ py: "var(--space-3)" }}>
      <Typography variant="body1">{title}</Typography>
      <Tooltip title={description} describeChild>
        <InfoOutlinedIcon
          tabIndex={0}
          role="img"
          aria-label={t("MoreInformation")}
          sx={{ fontSize: 18, color: "var(--color-text-tertiary)" }}
        />
      </Tooltip>
    </Inline>
  );

  const renderNotice = (message: string) => (
    <Typography
      variant="body2"
      color="textSecondary"
      sx={{ pt: "var(--space-4)" }}
    >
      {message}
    </Typography>
  );

  const renderTransferAccounts = () => {
    if (ledgerError) {
      return (
        <Inline gap="var(--space-4)" sx={{ pt: "var(--space-4)" }}>
          <Typography variant="body2" color="textSecondary">
            {t("LedgerAccountsLoadError")}
          </Typography>
          <Button size="small" onClick={() => refetchLedgerAccounts()}>
            {t("Retry")}
          </Button>
        </Inline>
      );
    }

    if (loadingExternalAccounts || loadingLedgerAccounts) {
      return (
        <Center sx={{ pt: "var(--space-6)" }}>
          <CircularProgress size="2rem" />
        </Center>
      );
    }

    if (externalAccounts.length === 0) {
      return renderNotice(t("NoBankAccountsToMap"));
    }

    if (ledgerAccounts.length === 0) {
      return renderNotice(t("NoLedgerAccountsFound"));
    }

    return (
      <Stack sx={{ pt: "var(--space-5)" }}>
        {externalAccounts.map((account) => renderTransferAccount(account))}
      </Stack>
    );
  };

  const renderTransferAccount = (account: BankAccount) => {
    const value = accountMappings[account.id] || "";
    const isKnown = ledgerAccounts.some((ledger) => ledger.id === value);
    const suggestion = suggestions[account.id];
    const suggested = ledgerAccounts.find((ledger) => ledger.id === suggestion);
    // Offered beside the field rather than filled into it, so what the field shows is always
    // what a save would write.
    const showSuggestion = !value && Boolean(suggested);

    return (
      <Columns key={account.id} count="minmax(0, 1fr) minmax(0, 1fr)">
        <Typography variant="body2" noWrap sx={{ pt: "var(--space-3)" }}>
          {accountLabel(account)}
        </Typography>
        <Stack gap="var(--space-2)">
          <Select
            aria-label={accountLabel(account)}
            size="md"
            fullWidth
            searchable
            clearable
            placeholder={transfersAccountName || t("NotMapped")}
            value={value || null}
            options={[
              ...(!isKnown && value
                ? [{ id: value, label: t("LedgerAccountUnavailable") }]
                : []),
              ...ledgerAccounts.map((ledger) => ({
                id: ledger.id,
                label: ledgerAccountLabel(ledger),
              })),
            ]}
            onChange={(next) =>
              setAccountMappings((previous) => ({
                ...previous,
                [account.id]: next ?? "",
              }))
            }
          />
          {showSuggestion && (
            <Inline gap="var(--space-3)">
              <Typography variant="caption" color="textSecondary">
                {t("SuggestedMatch", {
                  name: suggested && ledgerAccountLabel(suggested),
                })}
              </Typography>
              <Button
                size="small"
                onClick={() =>
                  setAccountMappings((previous) => ({
                    ...previous,
                    [account.id]: suggestion,
                  }))
                }
              >
                {t("Confirm")}
              </Button>
            </Inline>
          )}
        </Stack>
      </Columns>
    );
  };

  return (
    <>
      <Box sx={{ px: "var(--space-8)", py: "var(--space-7)" }}>
        {hasLoadError ? (
          <ErrorState
            title={t("Something went wrong")}
            description={t("SyncSettingsLoadError")}
            retryLabel={t("Retry")}
            onRetry={() => refetch()}
          />
        ) : (
          <>
            <Typography variant="h6">{t("WhatGetsSynced")}</Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ pt: "var(--space-2)" }}
            >
              {t("WhatGetsSyncedDescription")}
            </Typography>

            <RadioGroup
              sx={{ pt: "var(--space-5)" }}
              value={scope}
              onChange={(event) => setScope(event.target.value)}
            >
              <FormControlLabel
                value={SCOPE_EXPENSES_ONLY}
                control={<Radio />}
                label={renderChoice(
                  t("ExpensesOnly"),
                  t("ExpensesOnlyDescription"),
                )}
              />
              <FormControlLabel
                value={SCOPE_ALL_TRANSACTIONS}
                control={<Radio />}
                label={renderChoice(
                  t("AllTransactions"),
                  t("AllTransactionsDescription"),
                )}
              />
            </RadioGroup>

            {offersCardPosting && (
              <Box sx={{ pt: "var(--space-9)" }}>
                <Typography variant="h6">
                  {t("IntegrationOptionCardPosting")}
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ pt: "var(--space-2)" }}
                >
                  {t("IntegrationOptionCardPostingDescription")}
                </Typography>
                <RadioGroup
                  sx={{ pt: "var(--space-5)" }}
                  value={effectiveCardPosting}
                  onChange={(event) => setCardPosting(event.target.value)}
                >
                  {cardPostingOptions.map((option) => (
                    <FormControlLabel
                      key={option}
                      value={option}
                      control={<Radio />}
                      label={renderChoice(
                        t(CARD_POSTING_LABELS[option].title),
                        t(CARD_POSTING_LABELS[option].description),
                      )}
                    />
                  ))}
                </RadioGroup>
                {warnsCardAccount && (
                  <Typography
                    variant="body2"
                    sx={{ color: "var(--color-text-warning)" }}
                  >
                    {t("CardAccountFullSyncWarning")}
                  </Typography>
                )}
              </Box>
            )}

            {mapsTransfers && (
              <Box sx={{ pt: "var(--space-9)" }}>
                <Typography variant="h6">{t("TransferAccounts")}</Typography>

                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ pt: "var(--space-2)" }}
                >
                  {t("TransferAccountsDescription")}
                </Typography>
                {renderTransferAccounts()}
              </Box>
            )}
          </>
        )}
      </Box>

      {saveFailed && (
        <Typography
          variant="body2"
          color="error"
          sx={{ px: "var(--space-8)", pb: "var(--space-4)" }}
        >
          {t("SyncSettingsSaveError")}
        </Typography>
      )}

      {footerHost &&
        createPortal(
          <>
            <Button variant="primary-outlined" onClick={handleCloseDrawer}>
              {t("Cancel")}
            </Button>
            <Button
              variant="primary-filled"
              type="submit"
              disabled={!isLoaded || !isDirty || saving}
              onClick={handleSave}
            >
              {t("Save")}
            </Button>
          </>,
          footerHost,
        )}

      <LoadingWithoutModal open={loadingSettings || saving} />
    </>
  );
};

export default SyncSettingsDrawer;
